<script setup lang="ts">
  import type { ProductVariantPickerResource, ProductVariantSimpleResource } from '@storefront/resources';
  import { useHttpClient, useRoute } from '@othercommerce/foundation';
  import { Features } from '@shared/features';

  interface Props {
    variant: ProductVariantPickerResource,
  }

  defineProps<Props>();

  const http = useHttpClient();
  const route = useRoute();

  function onSelected(variant: ProductVariantSimpleResource) {
    http.dispatch('GET', route('web.product.picker.create', { product: variant.productId, variant: variant.id }), { preserveScroll: true, replace: true });
  }
</script>
<template>
  <UiModal class="picker" visitable open medium id="product-picker-modal" aria-labelledby="product-picker-modal-title">
    <section class="picker-container">
      <div class="picker-col centered">
        <MediaPicture :photo="variant.photo" sizes="300x300 2x min 577, 150x150 min 577, 150x150 2x, 75x75" />
      </div>
      <div class="picker-col">
        <SfHeading id="product-picker-modal-title" type="h2">{{ variant.title }}</SfHeading>
        <SfStars v-if="variant.reviews.count > 0" :rating="variant.reviews.average" :count="variant.reviews.count" />
        <SfProductCatalogPrice :item="variant" />
      </div>
    </section>
    <SfVariantPicker :variant @selected="onSelected" />
    <div class="row">
      <div class="col">
        <SfProductPrice :price="variant" presentation />
      </div>
      <div class="col stock">
        <SfProductStock :variant="variant" />
      </div>
    </div>
    <div class="picker-col medicinal" v-if="variant.isMedicinalProduct && Features.medicines.isEnabled && Features.medicalContractorsRegistry.isEnabled">
      {{ $t('SHOP.PRODUCT.MESSAGE.MEDICINAL_PRODUCT_NOTICE') }}
    </div>
    <SfProductOmnibus :price="variant" />
    <div class="actions">
      <SfCartStoreButton :variant="variant">
        <template #after v-if="variant">
          <SfIconCartAdd aria-hidden="true" />
        </template>
      </SfCartStoreButton>
      <SfButton back preserveScroll>
        {{ $t('SHOP.ACTION.CANCEL') }}
      </SfButton>
    </div>
  </UiModal>

  <RouterView />
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .picker-container {
    display: grid;
    grid-template-columns: 150px 1fr;
    align-items: start;
    gap: 1rem;

    .picker-col {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 0.5rem;

      h2 {
        font-size: 1.125rem;
      }
    }
  }

  .medicinal {
    font-size: 0.75rem;
  }

  .row {
    display: flex;
    justify-content: space-between;
    gap: 2rem;

    .col {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .stock {
      min-width: 0;
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @include media.query(sm) {
    .picker-container {
      grid-template-columns: 75px 1fr;

      .picker-col h2 {
        font-size: 1rem;
      }
    }

    .row {
      flex-direction: column;
      gap: 1rem;
    }
  }
</style>
